import { useState } from 'react';
import { useSelector } from 'react-redux';
import { Popup } from 'react-map-gl/maplibre';
import { Camera, ChevronDown, ChevronUp, Copy } from 'lucide-react';

import { MarkerInfoWindowButtons } from '../MarkerInfoWindowButtons';
import { OpenInMapsButton } from '../OpenInMapsButton';
import { BottomSheet } from '../../BottomSheet';
import { IconButtonTemplate } from '../../buttons/IconButtonTemplate';
import { CardRealty } from '../../realtyCard/CardRealty';
import { Loader } from '../../Loader';

import {
  realtyStatusMock,
  stageSelectDataMapValues,
} from '../../../../data/cardRealty';
import {
  colors,
  successMessages,
} from '../../../../data/constants';
import {
  formatNumberDisplay,
  generateStringValue,
  handleCopy,
} from '../../../../utils/utils';
import { generateCharacteristics } from '../../../../utils/ui';
import { useIsMobile } from '../../../../hooks/useMediaQuery';
import { selectUserToken } from '../../../../redux/features/user/userSelectors';
import { useGetRealtyQuery } from '../../../../redux/services/realties/realtiesApi';

const findStatus = value =>
  realtyStatusMock.find(s => Number(s.value) === Number(value));

const STAGE_DOTS = [
  { key: stageSelectDataMapValues.contact, color: colors.greenColor, label: 'Contact' },
  { key: stageSelectDataMapValues.introducer, color: colors.yellowColor, label: 'Introducer' },
  { key: stageSelectDataMapValues.urgent, color: colors.lightYellowColor, label: 'Urgent' },
  { key: stageSelectDataMapValues.visit, color: colors.lightPinkColor, label: 'Visit' },
];

const StageDots = ({ stages }) => {
  if (!stages?.length) return null;
  const active = STAGE_DOTS.filter(s => stages.some(x => x.value === s.key));
  if (!active.length) return null;
  return (
    <div className="flex items-center gap-1">
      {active.map(s => (
        <span
          key={s.key}
          title={s.label}
          className="inline-block w-2 h-2 rounded-full"
          style={{ backgroundColor: s.color }}
        />
      ))}
    </div>
  );
};

const InfoPreview = ({ realty, isMobile }) => {
  const {
    id,
    photo,
    property_name,
    city,
    street,
    street_number,
    living_area,
    garden,
    bedroom,
    bathroom,
    price,
    realty_status,
    realty_stages,
  } = realty;

  const address = generateStringValue([city, street, street_number]);
  const characteristics = generateCharacteristics({
    living_area,
    garden,
    bedroom,
    bathroom,
  });
  const status = findStatus(realty_status);

  const handleCopyMapUrl = () => {
    const url = `${window.location.origin}/map?highlight=${id}`;
    handleCopy(url, successMessages.copyLink);
  };

  return (
    <div className={`flex flex-col gap-2 ${isMobile ? 'p-3 w-full' : 'p-3 w-[340px]'}`}>
      <div className="flex gap-3">
        <div
          className={`shrink-0 flex items-center justify-center bg-gray-100 overflow-hidden rounded ${
            isMobile ? 'h-32 w-32' : 'h-24 w-24'
          }`}
        >
          {photo ? (
            <img
              alt=""
              src={photo}
              className="block w-full h-full object-cover object-center"
            />
          ) : (
            <Camera size={20} color={colors.darkGrayColor} />
          )}
        </div>

        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <h6 className="font-bold text-blackColor truncate text-sm">
              {`(${id}${property_name ? `, ${property_name}` : ''})`}
            </h6>
            {status && (
              <span
                className={`shrink-0 px-1.5 py-0.5 text-[10px] font-medium text-white rounded ${status.color}`}
                title={status.title}
              >
                {status.title}
              </span>
            )}
          </div>

          <span className="block text-blackColor text-sm">{address}</span>

          <span className="block text-gray-600 text-xs">{characteristics}</span>

          <div className="flex items-center gap-2 mt-0.5">
            <StageDots stages={realty_stages} />
          </div>
        </div>
      </div>

      <div className="flex justify-between items-center gap-2 w-full">
        <span className="font-semibold text-blackColor text-sm">
          {price ? `${formatNumberDisplay(price)} €` : '—'}
        </span>

        <div className="flex items-center gap-1.5">
          {isMobile && (
            <OpenInMapsButton
              latitude={realty.latitude}
              longitude={realty.longitude}
              label={address}
            />
          )}
          <IconButtonTemplate
            icon={Copy}
            size={14}
            isSmall={true}
            isRounded={true}
            tooltipText="Copy map link"
            handleClick={handleCopyMapUrl}
          />
          <MarkerInfoWindowButtons realty={realty} />
        </div>
      </div>

      {isMobile && (
        <div className="flex items-center justify-center gap-1 pt-1 text-gray-400 text-[11px]">
          <ChevronUp size={14} />
          <span>Swipe up for details</span>
        </div>
      )}
    </div>
  );
};

const InfoFull = ({ previewRealty }) => {
  const { id: realtyId, latitude, longitude, city, street, street_number } =
    previewRealty;
  const token = useSelector(selectUserToken);
  const { data: realty, isFetching } = useGetRealtyQuery(
    { token, id: realtyId },
    { skip: !token || !realtyId }
  );

  if (isFetching || !realty?.id) {
    return (
      <div className="flex justify-center items-center py-16">
        <Loader />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-[85vh] p-2">
      <div className="flex justify-end pb-2">
        <OpenInMapsButton
          latitude={latitude}
          longitude={longitude}
          label={generateStringValue([city, street, street_number])}
        />
      </div>
      <CardRealty realty={realty} isPwa={true} />
      <div className="flex items-center justify-center gap-1 mt-auto pt-6 pb-2 text-gray-400 text-[11px]">
        <ChevronDown size={14} />
        <span>Swipe down to view map</span>
      </div>
    </div>
  );
};

export const MarkerInfoWindow = ({
  realty = {},
  setActiveMarker = () => {},
}) => {
  const isMobile = useIsMobile();
  const [expanded, setExpanded] = useState(false);
  const { latitude, longitude } = realty;

  if (latitude == null || longitude == null) return null;

  const handleClose = () => {
    setExpanded(false);
    setActiveMarker(null);
  };

  if (isMobile) {
    return (
      <BottomSheet
        isOpen={true}
        onClose={handleClose}
        content={<InfoPreview realty={realty} isMobile={true} />}
        fullContent={expanded ? <InfoFull previewRealty={realty} /> : null}
        onSnapChange={snap => setExpanded(snap === 0.95)}
      />
    );
  }

  return (
    <Popup
      longitude={Number(longitude)}
      latitude={Number(latitude)}
      anchor="top"
      offset={16}
      closeOnClick={false}
      closeButton={false}
      onClose={handleClose}
      maxWidth="none"
    >
      <InfoPreview realty={realty} isMobile={false} />
    </Popup>
  );
};
